// Utilities & Hooks
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import fetchHandler from "../fetchHandler";
import { AdminClientsCreateRequestFields, AdminClientsListResponseFields } from "./interfaces";
import { AdminClientsEditFormDetailsState } from "../../pages/Admin/Clients/interfaces";

/**
 *
 * Get the list of ALL existing clients in the application
 *
 */
export function useAdminClientsGet() {
  return useQuery({
    queryKey: ["clients-admin"],
    queryFn: async () => {
      return (await fetchHandler("GET", "admin/company")) as AdminClientsListResponseFields[];
    },
    meta: {
      errorMessage: "Failed getting list of all existing clients",
    },
  });
}

/**
 *
 * Get a specific client based on the clients slug
 *
 */
export function useAdminClientsGetSpecific(clientSlug: string | undefined) {
  return useQuery({
    queryKey: ["client-admin", clientSlug],
    queryFn: async () => {
      return (await fetchHandler(
        "GET",
        `admin/company/${clientSlug}`,
      )) as AdminClientsListResponseFields;
    },
    enabled: !!clientSlug,
    meta: {
      errorMessage: "Failed getting details for targeted client",
    },

    // Do not store the data in cache, whenever the user re-visits
    // the same page with the same query keys, refetch the data so we can always be up-to-date
    gcTime: 0,
  });
}

/**
 *
 * Creates a new client
 *
 * The mutation hook takes the form's details as a parameter
 *
 */
export function useAdminClientCreate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clientDetails: AdminClientsCreateRequestFields) => {
      return fetchHandler<AdminClientsCreateRequestFields>("POST", "admin/company", clientDetails);
    },
    onSuccess: () => toast.success("Client created successfully!"),
    onError: error => error,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["clients-admin"] });
      queryClient.invalidateQueries({ queryKey: ["user-profile"] });
    },
  });
}

/**
 *
 * Updates the client details for a specific client
 *
 * @param slug The targeted client that will be updated
 *
 * The mutation hook takes the form's client details as a parameter
 */
export function useAdminClientUpdateDetails(slug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (details: AdminClientsEditFormDetailsState) => {
      return fetchHandler("PUT", `admin/company/${slug}`, details);
    },
    onSuccess: () => toast.success("Client's details were successfully updated"),
    onError: error => error,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["user-profile"] });
      return queryClient.invalidateQueries({ queryKey: ["clients-admin"] });
    },
  });
}
